
import { Node, mergeAttributes } from '@tiptap/core';

export interface VideoOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    video: {
      /**
       * Add a video
       */
      setVideo: (options: { src: string, provider: string }) => ReturnType;
    }
  }
}

export const Video = Node.create<VideoOptions>({
  name: 'video',
  
  group: 'block',
  
  atom: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
      provider: {
        default: 'youtube',
      },
      width: {
        default: '100%',
      },
      height: {
        default: 'auto',
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-video]',
        getAttrs: (node) => {
          if (typeof node === 'string') return {};
          console.log('Parsing div[data-video]:', node);
          const element = node as HTMLElement;
          const provider = element.getAttribute('data-video');
          const iframe = element.querySelector('iframe');
          
          if (!iframe) return {};
          
          const src = iframe.getAttribute('src');
          if (!src) return {};
          
          console.log('Found video iframe with src:', src);
          return {
            provider: provider || 'youtube',
            src: extractVideoId(src, provider || 'youtube')
          };
        }
      },
      // Parse iframe elements directly from saved content
      {
        tag: 'iframe[src*="youtube.com/embed"]',
        getAttrs: (node) => {
          if (typeof node === 'string') return {};
          console.log('Parsing youtube iframe:', node);
          const element = node as HTMLElement;
          const src = element.getAttribute('src');
          if (!src) return {};
          
          // Extract YouTube video ID from embed URL
          const match = src.match(/\/embed\/([^?&]+)/);
          console.log('Youtube match:', match);
          return {
            provider: 'youtube',
            src: match ? match[1] : '',
          };
        },
      },
      {
        tag: 'iframe[src*="player.vimeo.com"]',
        getAttrs: (node) => {
          if (typeof node === 'string') return {};
          console.log('Parsing vimeo iframe:', node);
          const element = node as HTMLElement;
          const src = element.getAttribute('src');
          if (!src) return {};
          
          // Extract Vimeo video ID from embed URL
          const match = src.match(/\/video\/(\d+)/);
          console.log('Vimeo match:', match);
          return {
            provider: 'vimeo',
            src: match ? match[1] : '',
          };
        },
      },
      {
        tag: 'iframe[src*="dailymotion.com/embed"]',
        getAttrs: (node) => {
          if (typeof node === 'string') return {};
          console.log('Parsing dailymotion iframe:', node);
          const element = node as HTMLElement;
          const src = element.getAttribute('src');
          if (!src) return {};
          
          // Extract Dailymotion video ID from embed URL
          const match = src.match(/\/video\/([a-zA-Z0-9]+)/);
          console.log('Dailymotion match:', match);
          return {
            provider: 'dailymotion',
            src: match ? match[1] : '',
          };
        },
      },
      // Detect video-wrapper divs containing iframes
      {
        tag: 'div.video-wrapper',
        getAttrs: (node) => {
          if (typeof node === 'string') return {};
          console.log('Parsing div.video-wrapper:', node);
          const element = node as HTMLElement;
          const iframe = element.querySelector('iframe');
          if (!iframe) return {};
          
          const src = iframe.getAttribute('src');
          if (!src) return {};
          
          console.log('Found iframe with src in video-wrapper:', src);
          
          let provider = 'youtube';
          let videoId = '';
          
          if (src.includes('youtube.com/embed')) {
            const match = src.match(/\/embed\/([^?&]+)/);
            videoId = match ? match[1] : '';
            provider = 'youtube';
          } else if (src.includes('player.vimeo.com')) {
            const match = src.match(/\/video\/(\d+)/);
            videoId = match ? match[1] : '';
            provider = 'vimeo';
          } else if (src.includes('dailymotion.com/embed')) {
            const match = src.match(/\/video\/([a-zA-Z0-9]+)/);
            videoId = match ? match[1] : '';
            provider = 'dailymotion';
          }
          
          console.log('Extracted video info:', { provider, videoId });
          
          return {
            provider,
            src: videoId,
          };
        },
      }
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const { provider, src } = HTMLAttributes;
    console.log('Rendering video with attrs:', HTMLAttributes);
    
    let embedSrc = src;
    
    if (provider === 'youtube') {
      embedSrc = `https://www.youtube.com/embed/${src}`;
    } else if (provider === 'vimeo') {
      embedSrc = `https://player.vimeo.com/video/${src}`;
    } else if (provider === 'dailymotion') {
      embedSrc = `https://www.dailymotion.com/embed/video/${src}`;
    }
    
    console.log('Final embed src:', embedSrc);
    
    return ['div', mergeAttributes(
      { class: 'video-wrapper', 'data-video': provider },
      this.options.HTMLAttributes,
    ), [
      'iframe',
      {
        src: embedSrc,
        frameborder: '0',
        allowfullscreen: 'true',
        allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
        style: 'position: absolute; top: 0; left: 0; width: 100%; height: 100%;',
      },
    ]]
  },

  addCommands() {
    return {
      setVideo: (options) => ({ commands }) => {
        console.log('Setting video with options:', options);
        return commands.insertContent({
          type: this.name,
          attrs: options,
        })
      },
    }
  },
})

// Helper function to extract video ID from embed URLs
export const extractVideoId = (src: string, provider: string): string => {
  console.log('Extracting video ID from:', src, 'for provider:', provider);
  let match;
  
  switch (provider) {
    case 'youtube':
      match = src.match(/\/embed\/([^?&]+)/);
      break;
    case 'vimeo':
      match = src.match(/\/video\/(\d+)/);
      break;
    case 'dailymotion':
      match = src.match(/\/video\/([a-zA-Z0-9]+)/);
      break;
    default:
      match = null;
  }
  
  console.log('Extraction result:', match ? match[1] : 'No match');
  return match ? match[1] : '';
}

export default Video;
